// for clickable grid, checkout:
// https://codepen.io/gaearon/pen/aWWQOG?editors=1111

import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import { CTX } from "../components/Store";
import * as d3 from "d3";
import { Icon } from "./Icon";
import { pAbr, a_names, aspRng01, aspRng02 } from "./util";

/* ------------------------------------------------------------------
   PURE HELPERS – moved outside component so they don't reallocate
---------------------------------------------------------------------*/

const getPrgHits = (nlng, plng, asp_rng, ptype) => {
  const hits = [];
  let pMax = nlng.length;

  // bypass pred asc/mc for tr & lp
  if (ptype === "tr" || ptype === "t2t" || ptype === "lp") {
    pMax = nlng.length - 2;
  }

  for (let n = 0; n < nlng.length; n++) {
    for (let p = 0; p < pMax; p++) {
      if (p === 11) continue; // skip node
      let ppl = plng[p];
      if (ppl < nlng[n]) ppl += 360;

      let arc = ppl - nlng[n];
      if (arc < 2) arc += 360;

      for (let a = 0; a < asp_rng.length; a++) {
        if (arc >= asp_rng[a][0] && arc < asp_rng[a][2]) {
          // p2: avoid self-aspects for outer planets
          if (ptype === "p2" && a === 0 && p > 4 && p === n) continue;

          hits.push({ p, a, n });
        }
      }
    }
  }
  return hits;
};

const getT2tHits = (nlng, plng, asp_rng) => {
  const hits = [];
  const nMax = nlng.length - 2;
  const pMax = nlng.length - 2;

  for (let n = 0; n < nMax; n++) {
    for (let p = 0; p < pMax; p++) {
      if (p === 11) continue;
      if (p >= n) continue;

      let ppl = plng[p];
      if (ppl < nlng[n]) ppl += 360;

      let arc = ppl - nlng[n];
      if (arc < 2) arc += 360;

      for (let a = 0; a < asp_rng.length; a++) {
        if (arc >= asp_rng[a][0] && arc < asp_rng[a][2]) {
          hits.push({ p, a, n });
        }
      }
    }
  }
  return hits;
};

/* ------------------------------------------------------------------
   REACT COMPONENT
---------------------------------------------------------------------*/

export default function Aspects() {
  const { st } = useContext(CTX);
  const aspcont = useRef(null);

  const side = 30;
  const rows = 15;
  const min = 0;
  const max = 450;

  /* ------------------------------------------------------------------
     Grid (memoized)
  ---------------------------------------------------------------------*/
  const gridVertical = useMemo(() => {
    return Array.from({ length: rows + 1 }, (_, i) => ({
      x1: i * side,
      y1: min,
      x2: i * side,
      y2: max,
    }));
  }, [rows]);

  const gridHorizontal = useMemo(() => {
    return Array.from({ length: rows + 1 }, (_, i) => ({
      x1: min,
      y1: i * side,
      x2: max,
      y2: i * side,
    }));
  }, [rows]);

  /* ------------------------------------------------------------------
     Planet Icon Offsets (memoized)
  ---------------------------------------------------------------------*/
  const p1 = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) =>
        `translate(0 ${i * side + side}) scale(.4)`
      ),
    []
  );

  const p2 = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) =>
        `translate(${i * side + side} 0) scale(.4)`
      ),
    []
  );

  /* ------------------------------------------------------------------
     USE EFFECT 1: NATÁL ASPECTS
  ---------------------------------------------------------------------*/
  useEffect(() => {
    if (!aspcont.current) return;
    const svg = d3.select(aspcont.current);
    svg.selectAll("*").remove();

    if (st.ptype !== "na") return;

    // Natal major aspects
    st.asps.forEach(([n, aIndex, p]) => {
      const pos = `translate(${p * side + 30} ${n * side + 30}) scale(1)`;
      const name = a_names[aIndex];

      svg
        .append("use")
        .attr("xlink:href", `/static/media/icons.${st.icons_id}.svg#${name}`)
        .attr("transform", pos);
    });

    // Declination aspects
    for (let i = 0; i < st.decl.length - 1; i++) {
      const d1 = Math.abs(st.decl[i]);
      for (let j = i + 1; j < st.decl.length; j++) {
        const d2 = Math.abs(st.decl[j]);
        if (Math.abs(d1 - d2) <= 1.0) {
          const pos = `translate(${i * side + 30} ${j * side + 30}) scale(1)`;
          const name =
            (st.decl[i] >= 0 && st.decl[j] <= 1) ||
            (st.decl[i] <= 0 && st.decl[j] >= 1)
              ? "Counter-Parallel"
              : "Parallel";

          svg
            .append("use")
            .attr("xlink:href", `/static/media/icons.${st.icons_id}.svg#${name}`)
            .attr("transform", pos);
        }
      }
    }
  }, [st.asps, st.decl, st.ptype, st.icons_id]);

  /* ------------------------------------------------------------------
     USE EFFECT 2: PROGRESSED / TRANSIT / T2T ASPECTS
  ---------------------------------------------------------------------*/
  useEffect(() => {
    if (!st.plng || st.plng.length === 0) return;
    if (!aspcont.current) return;

    const svg = d3.select(aspcont.current);
    svg.selectAll("*").remove();

    let aspRng = st.ptype === "lp" || st.ptype === "sa" ? aspRng02 : aspRng01;

    const hits =
      st.ptype === "t2t"
        ? getT2tHits(st.plng, st.plng, aspRng)
        : getPrgHits(st.lng, st.plng, aspRng, st.ptype);

    hits.forEach(({ p, a, n }) => {
      const pos = `translate(${n * side + 30} ${p * side + 30}) scale(1)`;
      const name = a_names[a];

      svg
        .append("use")
        .attr("xlink:href", `/static/media/icons.${st.icons_id}.svg#${name}`)
        .attr("transform", pos);
    });
  }, [st.ptype, st.lng, st.plng, st.icons_id]);

  /* ------------------------------------------------------------------
     JSX
  ---------------------------------------------------------------------*/
  return (
    <svg viewBox="0 0 490 490" xmlns="http://www.w3.org/2000/svg">
      <g stroke="rgb(10,10,10, .5)">
        {/* Grid */}
        {gridVertical.map((l, i) => (
          <line key={`v${i}`} {...l} strokeWidth="1" />
        ))}
        {gridHorizontal.map((l, i) => (
          <line key={`h${i}`} {...l} strokeWidth="1" />
        ))}

        {/* Planet glyphs */}
        {p1.map((t, i) => (
          <Icon key={`p1-${i}`} name={pAbr[i]} transf={t} />
        ))}
        {p2.map((t, i) => (
          <Icon key={`p2-${i}`} name={pAbr[i]} transf={t} />
        ))}

        {/* Dynamic Aspects (D3-rendered) */}
        <g ref={aspcont}></g>
      </g>
    </svg>
  );
}

